/**
 * DriverDashboard — driver-mode home. Mirrors Flutter `home_page.dart`
 * (driver branch) — quick actions + my trips + active shipments preview.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Truck, Package, Search, DollarSign, ScanLine, Plus, Calendar,
} from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { TripCard } from '@/components/trips/TripCard';
import { ShipmentCard } from '@/components/shipments/ShipmentCard';
import { useMyTrips } from '@/hooks/useTrips';
import { useDriverShipments } from '@/hooks/useShipments';

const TILES = [
  { to: '/driver/jobs',     icon: Package,   label: 'شحنات قريبة',    bg: '#0F2A5C', fg: '#FFFFFF' },
  { to: '/trips/create',    icon: Plus,      label: 'رحلة جديدة',     bg: '#F15A29', fg: '#FFFFFF' },
  { to: '/driver/lookup',   icon: ScanLine,  label: 'بحث برمز',       bg: '#16A34A', fg: '#FFFFFF' },
  { to: '/earnings',        icon: DollarSign, label: 'الأرباح',       bg: '#2E6B9E', fg: '#FFFFFF' },
];

export default function DriverDashboard() {
  const { t } = useTranslation();
  const { data: trips, isLoading: tripsLoading } = useMyTrips();
  const { data: shipments, isLoading: shipsLoading } = useDriverShipments();

  const activeTrips = (trips || [])
    .filter((t) => t.status === 'active' || t.status === 'in_progress' || t.status === 'pending')
    .slice(0, 2);
  const activeShipments = (shipments || [])
    .filter((s) => !['delivered', 'cancelled', 'returned'].includes(s.status))
    .slice(0, 4);

  return (
    <AppLayout>
      {/* Bento */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {TILES.map((tile) => (
          <Link
            key={tile.to}
            to={tile.to}
            className="rounded-2xl p-5 flex flex-col items-start gap-3 hover:opacity-90 transition shadow-sm"
            style={{ background: tile.bg, color: tile.fg }}
          >
            <tile.icon size={28} />
            <span className="font-bold text-sm md:text-base">
              {t(`driver_tile.${tile.to}`, tile.label)}
            </span>
          </Link>
        ))}
      </div>

      {/* Trips preview */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
            <Truck size={18} /> {t('my_trips', 'رحلاتي')}
          </h2>
          <Link to="/trips" className="text-primary text-sm hover:underline">
            {t('view_all', 'عرض الكل')}
          </Link>
        </div>
        {tripsLoading ? (
          <div className="bg-surface border border-border rounded-xl p-6 text-center text-text-tertiary">
            {t('loading', 'جاري التحميل...')}
          </div>
        ) : activeTrips.length === 0 ? (
          <div className="bg-surface border border-border rounded-xl p-6 text-center">
            <Calendar size={32} className="mx-auto text-text-tertiary mb-2" />
            <p className="text-text-secondary mb-3">{t('no_trips', 'لا رحلات نشطة')}</p>
            <Link
              to="/trips/create"
              className="inline-block bg-primary text-white rounded-xl px-4 py-2 text-sm font-semibold"
            >
              {t('create_first_trip', 'أنشئ أول رحلة')}
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {activeTrips.map((trip) => <TripCard key={trip.id} trip={trip} />)}
          </div>
        )}
      </section>

      {/* Driver shipments preview */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
            <Package size={18} /> {t('my_active_shipments', 'شحناتي النشطة')}
          </h2>
          <Link to="/driver/jobs" className="text-primary text-sm hover:underline flex items-center gap-1">
            <Search size={14} /> {t('browse_jobs', 'تصفّح الفرص')}
          </Link>
        </div>
        {shipsLoading ? (
          <div className="bg-surface border border-border rounded-xl p-6 text-center text-text-tertiary">
            {t('loading', 'جاري التحميل...')}
          </div>
        ) : activeShipments.length === 0 ? (
          <div className="bg-surface border border-border rounded-xl p-6 text-center">
            <Package size={32} className="mx-auto text-text-tertiary mb-2" />
            <p className="text-text-secondary mb-3">{t('no_active_driver_shipments', 'لا شحنات نشطة')}</p>
            <Link
              to="/driver/jobs"
              className="inline-block bg-secondary text-white rounded-xl px-4 py-2 text-sm font-semibold"
            >
              {t('find_jobs', 'ابحث عن فرص')}
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {activeShipments.map((s) => <ShipmentCard key={s.id} shipment={s} />)}
          </div>
        )}
      </section>
    </AppLayout>
  );
}
